import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Grid } from '@material-ui/core';
import { GatewayDefinition } from '@moderepo/mode-apis';
import { ITEMS_PER_PAGE_20 } from '@moderepo/mode-app-components';
import { fetchRCProjectGatewayCatalog, rcDataContext, selectProjectGatewayCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCRobotsListingContainer, RobotsTableHiddenFields } from './RCRobotsListingContainer';



/**
 * Define the list of fields we want to hide in the RobotsTable. This will depends on if there is a robot selected. If there is an robot
 * selected and are showing robot details on the Right side, we can hide some of the unnecessary columns.
 * This will create 2 set of RobotTableHiddenFields. The first set will show more column which should be used when there is no robot selected.
 * The second set has more columns hidden which is to be used when an robot is selected
 */
const robotsTableDisplayHiddenFields: RobotsTableHiddenFields = {
    overrideRobotDefinition: true,
    deviceId               : true,
    updatedAt              : true,
    availability           : true,
};



/**
 * This page Display ALL a Robots that are linked to a Device
 */
export const RCDeviceRobotsPage = () => {
    // Get the params from the route or url
    const params = useParams();
    const projectId = Number(params.projectId);
    const userId = params.userId ? Number(params.userId) : undefined;       // Only available if the agent is on the user details page
    const homeId = params.homeId ? Number(params.homeId) : undefined;       // Only available if the agent is on the user -> home details page
    const deviceId = params.deviceId ? Number(params.deviceId) : undefined;

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const selectedDevice = deviceId ? selectProjectDeviceById(dataState, deviceId) : undefined;
    const { trans } = useRitzTranslation(project);


    // Get Gateway Catalog
    const gatewayCatalog = selectProjectGatewayCatalog(rcDataState, projectId);

    // find the gatewayDefinition for the selected device from the gateway catalog. If we find a gatewayDefinition, that mean the device is a
    // gateway and we should show different UI.
    const isGateway = useMemo(() => {
        if (gatewayCatalog && selectedDevice) {
            return gatewayCatalog.find((gatewayDef: GatewayDefinition) => {
                return gatewayDef.deviceClass === selectedDevice.deviceClass;
            }) !== undefined;
        }

        return false;
    }, [gatewayCatalog, selectedDevice]);


    /**
     * For DeviceRobotsPage, we will only need to show the robots that are linked to the selected device
     */
    const [robotsTableDisplayFilters] = useState<{
        readonly homeId?: number;
        readonly deviceId?: number;
    }>({ deviceId });


    /**
     * This useEffect is used for loading gateway definition on component init
     */
    useEffect(() => {
        if (!gatewayCatalog && project) {
            rcDataDispatch(fetchRCProjectGatewayCatalog(project.id)).catch(() => {
                /* ignore error */
            });
        }
    }, [projectId, gatewayCatalog, project, rcDataDispatch]);


    /**
     * This useEffect is used for fetching device data on component load if we don't have device data yet
     */
    useEffect(() => {
        if (!selectedDevice && deviceId) {
            dataDispatch(fetchProjectDeviceById(projectId, deviceId)).catch(() => {
                // ignore error
            });
        }
    }, [dataDispatch, deviceId, projectId, selectedDevice]);


    return (
        <div className="page-main-content">
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.device_robots', { term: trans(isGateway ? 'terms.gateway' : 'terms.device') })}
                    </div>
                    <div className="page-subtitle">
                        {trans('page.subtitle.device_robots', { term: trans(isGateway ? 'terms.gateway' : 'terms.device') })}
                    </div>
                </div>
            </div>

            {/* All we need is a homeId, either from the routeParam or the selectedDevice.homeId, to get the list of robots  */}
            {project && (homeId || selectedDevice) && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <RCRobotsListingContainer
                            projectId={projectId}
                            userId={userId}
                            homeId={homeId || selectedDevice?.homeId}
                            deviceId={deviceId}
                            gatewayDependentRobotsOnly      // This page show robots that are linked to a device/gateway so this need to be true
                            itemsPerPage={ITEMS_PER_PAGE_20}
                            canLinkRobots={Boolean((homeId || selectedDevice?.homeId) && deviceId)}
                            canCreateRobots={Boolean(homeId || selectedDevice?.homeId)}
                            hiddenFields={robotsTableDisplayHiddenFields}
                            filters={robotsTableDisplayFilters}
                        />
                    </Grid>
                </Grid>
            )}

            {(!project || !(homeId || selectedDevice)) && (
                <CircularProgress />
            )}
        </div>
    );
};
